import React from 'react';
import { Box, FormControlLabel, Stack, Switch, Typography, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { UploadAiDefaults } from '../../../utils/uploadAiDefaults';

export interface AIEnrichmentStripProps {
  /** Current toggle state. */
  defaults: UploadAiDefaults;
  /** Called whenever a single toggle flips. Optional in read-only mode; required when interactive. */
  onChange?: (next: UploadAiDefaults) => void;
  /** Read-only mode (post-upload) renders chips instead of switches. */
  readOnly?: boolean;
  /** Right-side status text (e.g. "3 files processing…"). */
  status?: string;
}

const CHIP_LABEL: Record<keyof UploadAiDefaults, string> = {
  aiTitle: 'Title',
  aiDescription: 'Description',
  aiTags: 'Tags',
};

const KEYS: Array<keyof UploadAiDefaults> = ['aiTitle', 'aiDescription', 'aiTags'];

export const AIEnrichmentStrip: React.FC<AIEnrichmentStripProps> = ({
  defaults,
  onChange,
  readOnly,
  status,
}) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        px: 1.75,
        py: 1.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(180deg,#FAFCFE,#F2F8FB)'
          : 'linear-gradient(180deg,#0F1E33,#0B1828)',
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'brand.cyan' }} />
        AI enrichment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
        {readOnly ? 'filling' : 'will fill'}
      </Typography>

      {readOnly ? (
        <Stack direction="row" spacing={0.75}>
          {KEYS.map((k) => (
            <Chip
              key={k}
              size="small"
              label={defaults[k] ? CHIP_LABEL[k] : `${CHIP_LABEL[k]} off`}
              sx={(theme) => ({
                fontWeight: 700,
                fontSize: 11.5,
                backgroundColor: defaults[k]
                  ? 'rgba(34,181,220,.16)'
                  : theme.palette.action.disabledBackground,
                color: defaults[k] ? theme.palette.brand.teal : theme.palette.text.disabled,
              })}
            />
          ))}
        </Stack>
      ) : (
        <Stack direction="row" spacing={2}>
          {KEYS.map((k) => (
            <FormControlLabel
              key={k}
              control={
                <Switch
                  size="small"
                  checked={defaults[k]}
                  onChange={(e) => onChange?.({ ...defaults, [k]: e.target.checked })}
                />
              }
              label={<Typography variant="body2">{CHIP_LABEL[k]}</Typography>}
              sx={{ mr: 0 }}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ flex: 1 }} />
      {status && (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {status}
        </Typography>
      )}
    </Box>
  );
};
